import { useCallback, useEffect, useState } from 'react';

declare global {
  interface Window {
    Calendly?: any;
  }
}

export type CalendlyFormData = {
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  company?: string;
  message?: string;
  [key: string]: any;
};

export type CalendlyUtm = {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
};

type UseCalendlyPopupOptions = {
  url?: string; // 可选calendlyUrl
  utm?: CalendlyUtm; // utm参数
  onBooked?: () => void; // 预约成功回调
  onClose?: () => void; // 关闭回调
};

export function useCalendlyPopup(options: UseCalendlyPopupOptions = {}) {
  const { url, utm: defaultUtm, onBooked, onClose } = options;

  const [isReady, setIsReady] = useState(false);
  const [isBooked, setIsBooked] = useState(false);

  const calendlyUrl = url ?? 'https://calendly.com/adam-combs-matrix-lighting';

  /* --------------------------------
   * 加载 Calendly SDK（全局一次）
   * -------------------------------- */
  useEffect(() => {
    if (window.Calendly) {
      setIsReady(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    script.onload = () => setIsReady(true);

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://assets.calendly.com/assets/external/widget.css';

    document.head.appendChild(link);
    document.body.appendChild(script);

    return () => {
      // Cleanup if needed
    };
  }, []);

  /* --------------------------------
   * 监听 Calendly 事件
   * -------------------------------- */
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (!e.data?.event) return;

      // 预约成功
      if (e.data.event === 'calendly.event_scheduled') {
        setIsBooked(true);
        onBooked?.();
      }

      // 弹窗关闭
      if (e.data.event === 'calendly.event_type_viewed') {
        // Calendly popup opened
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onBooked, onClose]);

  /* --------------------------------
   * 打开 Calendly Popup
   * -------------------------------- */
  const openCalendly = useCallback(
    (formData?: CalendlyFormData, utm?: CalendlyUtm) => {
      if (!isReady || !window.Calendly) {
        console.warn('Calendly SDK not ready yet');
        return;
      }

      // 预填充数据
      const prefill: any = {};

      if (formData) {
        // name
        if (formData.firstName && formData.lastName) {
          prefill.name = `${formData.firstName} ${formData.lastName}`;
        } else if (formData.name) {
          prefill.name = formData.name;
        }

        // email
        if (formData.email) prefill.email = formData.email;

        // 自定义字段（需要 Calendly 后台配置question
        const customAnswers: Record<string, string> = {};
        // additional notes
        if (formData.message) customAnswers.a1 = formData.message;

        if (Object.keys(customAnswers).length > 0) {
          prefill.customAnswers = customAnswers;
        }
      }

      /* -----------------------------
       * utm 参数
       * ----------------------------- */
      const mergedUtm = {
        ...defaultUtm,
        ...utm,
      };

      window.Calendly.initPopupWidget({
        url: calendlyUrl + `?primary_color=521bdb&text_color=521bdb&hide_event_type_details=true&hide_gdpr_banner=true`,
        prefill,
        utm: Object.keys(mergedUtm).length ? mergedUtm : undefined,
      });
    },
    [isReady, calendlyUrl],
  );

  /* --------------------------------
   * 重置预约状态
   * -------------------------------- */
  const reset = useCallback(() => {
    setIsBooked(false);
  }, []);

  return {
    openCalendly,
    reset,
    isReady,
    isBooked,
  };
}
